import React from "react";

const TheProblem = () => {
  const problems = [
    {
      icon: "heart_broken",
      title: "Broken & Static Forms",
      desc: "Traditional forms break easily and offer zero flexibility. If a field changes, your whole backend might too.",
    },
    {
      icon: "all_inbox",
      title: "Scattered Conversations",
      desc: "Messages hidden in Gmail, WhatsApp, and Slack. Leads fall through the cracks because they aren't centralized.",
    },
    {
      icon: "timer_off",
      title: "The 'Waiting' Tax",
      desc: "Slow response times kill conversions. Without automation, your team spends hours just sorting through noise.",
    },
    {
      icon: "payments",
      title: "Expensive Backend Dev",
      desc: "Building your own form handling is costly and time-consuming. You shouldn't need a developer just to read a message.",
    },
  ];

  return (
    <section
      id="the-problem"
      className="py-16 sm:py-20 lg:py-24 bg-[#f8fafc] border-t border-[#f1f5f9] overflow-hidden"
    >
      <div className="mx-auto w-[95%] md:w-[90%]">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
          <div className="w-full lg:w-1/2">
            <div className="inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-medium bg-[#e11d48]/10 text-[#e11d48] mb-5 sm:mb-6">
              The Reality of Support
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0f172a] sm:text-4xl lg:text-5xl leading-tight animate-fade-in-up">
              When Connecting with Customers{" "}
              <br className="hidden sm:block" />
              <span className="text-[#e11d48]">Becomes Complicated.</span>
            </h2>

            <p className="mt-5 sm:mt-6 text-base sm:text-lg leading-relaxed text-[#334155] animate-fade-in-up [animation-delay:100ms]">
              For most teams in India, managing customer inquiries feels like
              fighting a losing battle. Between broken code and scattered
              inboxes, the human connection is often lost in the chaos.
            </p>

            <ul className="mt-8 sm:mt-10 space-y-6 sm:space-y-8">
              {problems.map((item, index) => (
                <li
                  key={index}
                  className="group flex gap-3 sm:gap-4 opacity-0 animate-fade-in-up"
                  style={{ animationDelay: `${200 + index * 100}ms` }}
                >
                  <div className="flex-none flex items-center justify-center size-9 sm:size-10 rounded-lg bg-white border border-[#f1f5f9] text-[#e11d48] shadow-sm group-hover:bg-[#e11d48] group-hover:text-white transition-all duration-300">
                    <span className="material-symbols-outlined text-lg sm:text-xl">
                      {item.icon}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-[#0f172a] text-sm sm:text-base">
                      {item.title}
                    </h3>
                    <p className="text-[#64748b] text-sm mt-1">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-[#f1f5f9] animate-fade-in-up [animation-delay:600ms]">
              <p className="text-sm font-semibold italic text-[#1e3a8a]">
                "That’s where ContactHQ steps in — an intelligent, no-fuss
                solution for building forms and managing responses."
              </p>
            </div>
          </div>

          <div className="w-full lg:w-1/2 relative opacity-0 animate-fade-in-up [animation-delay:400ms] pb-14 sm:pb-10">
            <div className="relative rounded-2xl sm:rounded-3xl bg-white border border-[#f1f5f9] p-3 sm:p-4 shadow-2xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#e11d48]/5 to-[#1e3a8a]/5 z-0"></div>

              <div className="relative z-10 space-y-3 sm:space-y-4">
                <div className="p-3 sm:p-4 bg-white/60 border border-red-100 rounded-xl blur-[1px] flex justify-between items-center gap-2 scale-95 opacity-60">
                  <div className="flex gap-3 min-w-0">
                    <div className="size-8 shrink-0 rounded-full bg-red-100 animate-pulse"></div>
                    <div className="space-y-2 min-w-0">
                      <div className="h-2 w-20 sm:w-24 bg-red-200 rounded"></div>
                      <div className="h-2 w-28 sm:w-32 bg-slate-100 rounded"></div>
                    </div>
                  </div>
                  <div className="text-red-400 font-mono text-[10px] shrink-0">
                    ERROR 404
                  </div>
                </div>

                <div className="flex justify-center py-1 sm:py-2">
                  <span className="material-symbols-outlined text-[#e11d48] animate-bounce">
                    expand_more
                  </span>
                </div>

                <div className="p-4 sm:p-5 bg-white border-2 border-[#e11d48]/20 rounded-2xl shadow-xl flex justify-between items-center gap-2">
                  <div className="flex gap-3 sm:gap-4 min-w-0">
                    <div className="size-10 sm:size-12 shrink-0 rounded-full bg-[#1e3a8a] text-white flex items-center justify-center font-bold text-sm">
                      AP
                    </div>
                    <div className="space-y-1 min-w-0">
                      <h4 className="text-sm font-bold text-[#0f172a] truncate">
                        Arjun Patel
                      </h4>
                      <p className="text-xs text-[#64748b] line-clamp-1">
                        "Love at First Sight" Project Query
                      </p>
                    </div>
                  </div>
                  <div className="px-2 sm:px-3 py-1 rounded-full bg-green-50 text-green-600 text-[9px] sm:text-[10px] font-bold shrink-0 whitespace-nowrap">
                    NEW LEAD
                  </div>
                </div>

                <div className="p-4 sm:p-5 bg-white border border-[#f1f5f9] rounded-2xl shadow-md flex justify-between items-center gap-2 sm:translate-x-4">
                  <div className="flex gap-3 sm:gap-4 min-w-0">
                    <div className="size-10 sm:size-12 shrink-0 rounded-full bg-slate-100 text-[#0f172a] flex items-center justify-center font-bold text-sm">
                      RK
                    </div>
                    <div className="space-y-1 min-w-0">
                      <h4 className="text-sm font-bold text-[#0f172a] truncate">
                        Rahul K.
                      </h4>
                      <p className="text-xs text-[#64748b] line-clamp-1">
                        Website Design Inquiry
                      </p>
                    </div>
                  </div>
                  <div className="px-2 sm:px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-[9px] sm:text-[10px] font-bold shrink-0 whitespace-nowrap">
                    REPLIED
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-10 -right-10 size-40 bg-[#1e3a8a]/10 blur-[60px] rounded-full"></div>
            </div>

            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 sm:left-auto sm:right-6 sm:translate-x-0 sm:-bottom-4">
              <a
                href="#how-it-works"
                className="rounded-full bg-[#0f172a] px-5 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-white shadow-xl hover:bg-[#e11d48] transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
              >
                See How It Works
                <span className="material-symbols-outlined text-sm">
                  arrow_forward
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TheProblem;
